import { lazy, Suspense, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { FileText, Languages, Mic, Mic2, Globe, BarChart3, Shield, Wand2, RefreshCw, Subtitles, History, LucideIcon } from 'lucide-react';
import Header from './components/Header';
import { Login } from './components/Login';
import { useAuthMeta } from './components/AuthProvider';
import { PremierePromo, PremiereExtensionPage } from './components/PremiereExtension';

// Each tool tab is code-split so first paint only downloads the active
// tool instead of every tool (and its heavy deps) at once.
const TranscriptionTool = lazy(() => import('./components/TranscriptionTool'));
const HinglishTranslator = lazy(() => import('./components/HinglishTranslator'));
const HinglishSubsTool = lazy(() => import('./components/HinglishSubsTool'));
const VocalExtractor = lazy(() => import('./components/VocalExtractor'));
const EnglishTranslationTool = lazy(() => import('./components/EnglishTranslationTool'));
const SoundEffectsGenerator = lazy(() => import('./components/SoundEffectsGenerator'));
const VoiceGenerator = lazy(() => import('./components/VoiceGenerator'));
const SrtImprover = lazy(() => import('./components/SrtImprover'));
const UsageDashboard = lazy(() => import('./components/UsageDashboard'));
const HistoryPanel = lazy(() => import('./components/HistoryPanel'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));

const TabSpinner = () => (
  <div className="flex justify-center py-20">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
  </div>
);

interface TabConfig {
  value: string;
  label: string;
  icon: LucideIcon;
  content: JSX.Element;
  adminOnly?: boolean;
}

// The tab the app opens on. Must match a `value` below that isn't adminOnly.
const DEFAULT_TAB = 'hinglish-subs';

const TABS: TabConfig[] = [
  { value: 'transcription', label: 'Transcription', icon: FileText, content: <TranscriptionTool /> },
  { value: 'translation', label: 'Hindi to Hinglish', icon: Languages, content: <HinglishTranslator /> },
  { value: 'hinglish-subs', label: 'Hinglish Subs', icon: Subtitles, content: <HinglishSubsTool /> },
  { value: 'vocal-extractor', label: 'Vocal Extractor', icon: Mic, content: <VocalExtractor /> },
  { value: 'english-translation', label: 'Translated in English', icon: Globe, content: <EnglishTranslationTool /> },
  { value: 'sound-effects', label: 'Sound Effects', icon: Wand2, content: <SoundEffectsGenerator /> },
  { value: 'voice-generation', label: 'Voice Generation', icon: Mic2, content: <VoiceGenerator /> },
  { value: 'srt-improver', label: 'SRT Improver', icon: RefreshCw, content: <SrtImprover /> },
  { value: 'history', label: 'History', icon: History, content: <HistoryPanel /> },
  { value: 'usage', label: 'Usage', icon: BarChart3, content: <UsageDashboard /> },
  { value: 'admin', label: 'Admin', icon: Shield, content: <AdminDashboard />, adminOnly: true },
];

const tabTriggerClass = (isActive: boolean) =>
  `flex-1 px-3 md:px-4 py-3 text-xs md:text-sm font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all duration-300 whitespace-nowrap ${isActive
    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20 transform scale-[1.02]'
    : 'text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-100/50 dark:hover:bg-slate-800/50'
  }`;

function App() {
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  // Auth + authorization come from the backend session (/api/auth/me).
  const { user, loading, isAdmin, approved, logout } = useAuthMeta();
  const isAllowed = approved;

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is not logged in, show login page
  if (!user) {
    return <Login />;
  }

  // If user is logged in but not allowed, show login page with denied state
  if (!isAllowed) {
    return <Login user={user} />;
  }

  const visibleTabs = TABS.filter((tab) => !tab.adminOnly || isAdmin);
  // The Premiere extension's help page (the panel's Help button opens it).
  const isPremierePage = window.location.pathname.replace(/\/+$/, '') === '/premiere';

  return (
    <div className="bg-mesh-light dark:bg-mesh-dark min-h-screen text-slate-800 dark:text-slate-100 transition-colors duration-500 relative">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>
      <div className="absolute inset-0 bg-white/40 dark:bg-slate-950/40 pointer-events-none"></div>
      <Header />
      <main id="main-content" className="container mx-auto px-4 py-10 max-w-5xl relative z-10">
        {isPremierePage ? (
          <PremiereExtensionPage />
        ) : (
        <>
        <PremierePromo />
        <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
          <Tabs.List className="flex flex-wrap md:flex-nowrap space-x-0 md:space-x-1 space-y-1 md:space-y-0 glass-panel p-1.5 rounded-2xl mb-10 overflow-x-auto hide-scrollbar sticky top-[72px] z-40">
            {visibleTabs.map(({ value, label, icon: Icon }) => (
              <Tabs.Trigger key={value} value={value} className={tabTriggerClass(activeTab === value)}>
                <Icon size={16} />
                <span>{label}</span>
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <div className="animate-fade-in">
            <Suspense fallback={<TabSpinner />}>
              {visibleTabs.map(({ value, content }) => (
                <Tabs.Content key={value} value={value}>
                  {content}
                </Tabs.Content>
              ))}
            </Suspense>
          </div>
        </Tabs.Root>
        </>
        )}
      </main>
      <footer className="relative z-10 glass-panel border-x-0 border-b-0 text-slate-600 dark:text-slate-400 py-8 mt-12 transition-colors duration-300">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm">
              &copy; {new Date().getFullYear()} TRB's Subtitle Studio. All rights reserved.
              <a href="/premiere" className="ml-3 hover:text-indigo-600 dark:hover:text-indigo-400 underline-offset-2 hover:underline">
                Premiere Pro extension
              </a>
            </p>
            <button
              type="button"
              onClick={() => logout()}
              className="text-sm mt-4 md:mt-0 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
